import React, { useEffect, useState, useCallback } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { useStudentAuth } from '../../hooks/useStudentAuth';
import { supabase } from '../../lib/supabase';
import { resolveFileUrl } from '../../lib/portalHelpers';
import type { PortalClass, Recording, Material, Certificate } from '../../types/portal';
import {
  ChevronLeft,
  ChevronRight,
  CheckCircle2,
  FileText,
  Link as LinkIcon,
  BookOpen,
  Clock,
  Award,
  Download,
} from 'lucide-react';
import CourseCover from '../../components/CourseCover';
import LessonRow from '../../components/student/LessonRow';
import DiscordIcon from '../../components/icons/DiscordIcon';
import WhatsAppIcon from '../../components/icons/WhatsAppIcon';
import { isNewRecording } from '../../lib/portalHelpers';

const PREVIEW_LESSON_COUNT = 3;

const StudentCoursePage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { user } = useStudentAuth();

  const [cls, setCls] = useState<PortalClass | null>(null);
  const [recordings, setRecordings] = useState<Recording[]>([]);
  const [materials, setMaterials] = useState<Material[]>([]);
  const [certificates, setCertificates] = useState<Certificate[]>([]);
  const [completedIds, setCompletedIds] = useState<Set<string>>(new Set());
  const [watchedIds, setWatchedIds] = useState<Set<string>>(new Set());
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [toggling, setToggling] = useState<string | null>(null);
  const [downloadError, setDownloadError] = useState<string | null>(null);

  const openFile = async (
    bucket: 'material-files' | 'certificates',
    storagePath: string | null | undefined,
    fallbackUrl: string | null | undefined,
  ) => {
    setDownloadError(null);
    const target = await resolveFileUrl(bucket, storagePath, fallbackUrl);
    if (target) {
      window.open(target, '_blank', 'noopener,noreferrer');
    } else {
      setDownloadError('Die Datei konnte nicht geöffnet werden. Bitte versuche es erneut oder kontaktiere uns.');
    }
  };

  const openLesson = (rec: Recording) => {
    setWatchedIds(prev => new Set(prev).add(rec.id));
    navigate(`/dashboard/kurse/${id}/lektion/${rec.id}`);
  };

  const load = useCallback(async (signal: { cancelled: boolean }) => {
    if (!id) return;
    setLoadError(null);
    setLoading(true);
    try {
      const [
        { data: classData, error: classError },
        { data: recData, error: recError },
        { data: matData },
        { data: progressData },
        { data: certData },
        { data: positionsData },
      ] = await Promise.all([
        supabase.from('portal_classes').select('*').eq('id', id).single(),
        supabase
          .from('recordings')
          .select('*')
          .eq('class_id', id)
          .eq('is_visible', true)
          .order('sort_order', { ascending: true })
          .order('recorded_at', { ascending: true })
          .order('created_at', { ascending: true }),
        supabase
          .from('material_classes')
          .select('materials(*)')
          .eq('class_id', id),
        user?.id
          ? supabase.from('recording_progress').select('recording_id').eq('student_id', user.id)
          : Promise.resolve({ data: [] as { recording_id: string }[], error: null }),
        user?.id
          ? supabase.from('certificates').select('*').eq('class_id', id).eq('student_id', user.id)
          : Promise.resolve({ data: [] as Certificate[], error: null }),
        user?.id
          ? supabase.from('recording_positions').select('recording_id').eq('student_id', user.id)
          : Promise.resolve({ data: [] as { recording_id: string }[], error: null }),
      ]);

      if (signal.cancelled) return;
      if (classError || !classData) { navigate('/dashboard'); return; }
      if (recError) throw recError;

      const materialRows = ((matData ?? []) as Array<{ materials: Material | null }>)
        .map(r => r.materials)
        .filter((m): m is Material => !!m && m.is_visible);

      setCls(classData as PortalClass);
      setRecordings((recData ?? []) as Recording[]);
      setMaterials(materialRows);
      setCertificates((certData ?? []) as Certificate[]);
      setCompletedIds(
        new Set((progressData ?? []).map((p: { recording_id: string }) => p.recording_id))
      );
      setWatchedIds(
        new Set((positionsData ?? []).map((p: { recording_id: string }) => p.recording_id))
      );
      setLoading(false);
    } catch (err) {
      if (signal.cancelled) return;
      if (import.meta.env.DEV) console.error(err);
      setLoadError('Kursdaten konnten nicht geladen werden. Bitte versuche es erneut.');
      setLoading(false);
    }
  }, [id, navigate, user?.id]);

  useEffect(() => {
    const signal = { cancelled: false };
    load(signal);
    return () => { signal.cancelled = true; };
  }, [load]);

  const toggleCompleted = async (recordingId: string) => {
    if (!user || toggling) return;
    setToggling(recordingId);
    const isDone = completedIds.has(recordingId);

    setCompletedIds(prev => {
      const next = new Set(prev);
      if (isDone) next.delete(recordingId);
      else next.add(recordingId);
      return next;
    });

    if (isDone) {
      await supabase
        .from('recording_progress')
        .delete()
        .eq('recording_id', recordingId)
        .eq('student_id', user.id);
    } else {
      await supabase
        .from('recording_progress')
        .insert({ student_id: user.id, recording_id: recordingId });
    }
    setToggling(null);
  };

  if (loading) {
    return (
      <div className="space-y-4">
        <div className="h-5 w-28 bg-gray-200 dark:bg-neutral-800 rounded animate-pulse" />
        <div className="h-8 w-64 bg-gray-200 dark:bg-neutral-800 rounded animate-pulse" />
        <div className="h-24 bg-white dark:bg-neutral-900 rounded-2xl border border-gray-200 dark:border-neutral-800 animate-pulse" />
        <div className="h-48 bg-white dark:bg-neutral-900 rounded-2xl border border-gray-200 dark:border-neutral-800 animate-pulse" />
      </div>
    );
  }

  if (loadError) {
    return (
      <div className="space-y-4">
        <button onClick={() => navigate('/dashboard')} className="flex items-center gap-1.5 text-sm font-semibold text-gray-500 dark:text-neutral-400 hover:text-[#970000] dark:hover:text-red-400 transition-colors">
          <ChevronLeft className="h-4 w-4" />Zurück zu Meine Kurse
        </button>
        <div className="flex items-start gap-3 p-5 bg-gray-50 dark:bg-neutral-800 border border-gray-200 dark:border-neutral-800 rounded-2xl">
          <BookOpen className="h-5 w-5 text-[#970000] dark:text-red-400 shrink-0 mt-0.5" />
          <div className="flex-1">
            <p className="text-sm font-medium text-gray-900 dark:text-neutral-100">{loadError}</p>
          </div>
          <button onClick={() => load({ cancelled: false })} className="text-xs text-[#970000] dark:text-red-400 font-semibold underline hover:no-underline shrink-0">
            Erneut versuchen
          </button>
        </div>
      </div>
    );
  }

  if (!cls) return null;

  const total = recordings.length;
  const completed = recordings.filter(r => completedIds.has(r.id)).length;
  const pct = total > 0 ? Math.round((completed / total) * 100) : 0;
  const isArchived = !!cls.archived_at;

  const statusLabel =
    pct === 100
      ? 'Abgeschlossen'
      : pct > 0
        ? 'In Bearbeitung'
        : isArchived
          ? 'Kurs beendet'
          : 'Noch nicht begonnen';

  const statusStyle =
    pct === 100
      ? 'bg-gray-50 dark:bg-neutral-800 text-gray-500 dark:text-neutral-400 border border-gray-200 dark:border-neutral-800'
      : 'bg-gray-50 dark:bg-neutral-800 text-gray-500 dark:text-neutral-400 border border-gray-200 dark:border-neutral-800';

  // Show only the most recent N lessons; rest goes to dedicated lessons page
  const hasMoreLessons = total > PREVIEW_LESSON_COUNT;
  const previewRecordings = hasMoreLessons
    ? recordings.slice(-PREVIEW_LESSON_COUNT)
    : recordings;
  const previewStartIndex = hasMoreLessons ? total - PREVIEW_LESSON_COUNT : 0;

  return (
    <>
      <div className="space-y-5">
        <button
          onClick={() => navigate('/dashboard')}
          className="flex items-center gap-1.5 text-sm font-semibold text-gray-500 dark:text-neutral-400 hover:text-[#970000] dark:hover:text-red-400 transition-colors"
        >
          <ChevronLeft className="h-4 w-4" />
          Zurück zu Meine Kurse
        </button>

        {downloadError && (
          <div className="flex items-start gap-3 p-4 bg-[#970000]/5 dark:bg-red-500/10 border border-[#970000]/20 dark:border-red-500/40 rounded-2xl">
            <BookOpen className="h-5 w-5 text-[#970000] dark:text-red-400 shrink-0 mt-0.5" />
            <p className="flex-1 text-sm text-gray-900 dark:text-neutral-100">{downloadError}</p>
            <button
              onClick={() => setDownloadError(null)}
              className="text-xs text-gray-400 dark:text-neutral-500 font-semibold hover:text-gray-600 dark:hover:text-neutral-300 shrink-0"
            >
              Schließen
            </button>
          </div>
        )}

        {/* Header with cover */}
        <div className="flex items-start gap-4">
          <CourseCover cls={cls} size={96} />
          <div className="flex-1 min-w-0 pt-1">
            <div className="flex flex-wrap items-center gap-1.5 mb-2">
              {(cls.category || cls.level) && (
                <span className="text-[10px] font-bold tracking-wider uppercase bg-gray-900 dark:bg-neutral-100 text-white dark:text-neutral-900 px-2 py-0.5 rounded-md">
                  {cls.category ?? cls.level}
                </span>
              )}
              <span
                className={`inline-flex items-center gap-1 text-[10px] font-semibold px-2 py-0.5 rounded-md ${statusStyle}`}
              >
                {pct === 100 && <CheckCircle2 className="h-3 w-3" />}
                {statusLabel}
              </span>
              {cls.schedule && (
                <span className="inline-flex items-center gap-1 text-[10px] font-semibold px-2 py-0.5 rounded-md bg-gray-50 dark:bg-neutral-800 text-gray-500 dark:text-neutral-400 border border-gray-200 dark:border-neutral-800">
                  <Clock className="h-3 w-3 text-[#970000] dark:text-red-400" />
                  {cls.schedule}
                </span>
              )}
            </div>
            <h1 className="text-xl sm:text-2xl font-bold text-gray-900 dark:text-neutral-100 leading-snug">
              {cls.title}
            </h1>
            {cls.description && (
              <p className="text-sm text-gray-500 dark:text-neutral-400 mt-1.5 line-clamp-2">{cls.description}</p>
            )}
            {cls.discord_link && (
              <a
                href={cls.discord_link}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-1.5 mt-2 mr-3 text-xs font-semibold text-[#5865F2] hover:underline"
              >
                <DiscordIcon className="h-3.5 w-3.5" />
                Discord-Gruppe öffnen
              </a>
            )}
            {cls.whatsapp_link && (
              <a
                href={cls.whatsapp_link}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-1.5 mt-2 text-xs font-semibold text-[#1da851] hover:underline"
              >
                <WhatsAppIcon className="h-3.5 w-3.5" />
                WhatsApp-Gruppe öffnen
              </a>
            )}
          </div>
        </div>

        {/* Progress card */}
        <div className="bg-white dark:bg-neutral-900 rounded-2xl border border-gray-200 dark:border-neutral-800 p-5">
          <div className="flex items-center justify-between mb-3">
            <span className="text-sm font-semibold text-gray-900 dark:text-neutral-100">Fortschritt</span>
            <span className="text-sm text-gray-500 dark:text-neutral-400">
              {completed} / {total} Lektionen ·{' '}
              <span className="font-bold text-gray-900 dark:text-neutral-100">{pct}%</span>
            </span>
          </div>
          <div className="h-2 bg-gray-200 dark:bg-neutral-800 rounded-full overflow-hidden">
            <div
              className="h-full bg-[#970000] rounded-full transition-all duration-500"
              style={{ width: `${pct}%` }}
            />
          </div>
        </div>

        {/* Lessons preview */}
        <div className="bg-white dark:bg-neutral-900 rounded-2xl border border-gray-200 dark:border-neutral-800 overflow-hidden">
          <div className="px-5 pt-5 pb-3 border-b border-gray-200 dark:border-neutral-800">
            <p className="text-[10px] font-bold tracking-[0.14em] uppercase text-gray-400 dark:text-neutral-500 mb-0.5">
              Inhalt
            </p>
            <h2 className="text-lg font-bold text-gray-900 dark:text-neutral-100">
              {hasMoreLessons ? 'Neueste Lektionen' : 'Lektionen & Videos'}
            </h2>
          </div>

          {recordings.length === 0 ? (
            <div className="p-6 text-center">
              <BookOpen className="h-8 w-8 text-gray-300 dark:text-neutral-600 mx-auto mb-2" />
              <p className="text-sm text-gray-400 dark:text-neutral-500">Noch keine Lektionen hinterlegt.</p>
            </div>
          ) : (
            <>
              <ul className="divide-y divide-gray-200 dark:divide-neutral-800">
                {previewRecordings.map((rec, i) => (
                  <LessonRow
                    key={rec.id}
                    recording={rec}
                    number={previewStartIndex + i + 1}
                    done={completedIds.has(rec.id)}
                    isNew={isNewRecording(rec, completedIds.has(rec.id) || watchedIds.has(rec.id))}
                    toggling={toggling === rec.id}
                    onToggleCompleted={() => toggleCompleted(rec.id)}
                    onPlay={() => openLesson(rec)}
                  />
                ))}
              </ul>
              {hasMoreLessons && (
                <Link
                  to={`/dashboard/kurse/${id}/lektionen`}
                  className="w-full flex items-center justify-center gap-1.5 px-4 py-3 text-xs font-semibold text-[#970000] dark:text-red-400 hover:bg-gray-50 dark:hover:bg-neutral-800 border-t border-gray-200 dark:border-neutral-800 transition-colors"
                >
                  Alle {total} Lektionen ansehen
                  <ChevronRight className="h-3.5 w-3.5" />
                </Link>
              )}
            </>
          )}
        </div>

        {/* Certificates */}
        {certificates.length > 0 && (
          <div className="bg-white dark:bg-neutral-900 rounded-2xl border border-gray-200 dark:border-neutral-800 overflow-hidden">
            <div className="px-5 pt-5 pb-3 border-b border-gray-200 dark:border-neutral-800">
              <p className="text-[10px] font-bold tracking-[0.14em] uppercase text-gray-400 dark:text-neutral-500 mb-0.5">
                Erfolg
              </p>
              <h2 className="text-lg font-bold text-gray-900 dark:text-neutral-100">Dein Zertifikat</h2>
            </div>
            <ul className="divide-y divide-gray-200 dark:divide-neutral-800">
              {certificates.map(cert => (
                <li key={cert.id}>
                  <button
                    type="button"
                    onClick={() => openFile('certificates', cert.storage_path, cert.file_url)}
                    className="w-full text-left flex items-center gap-3 px-4 py-3.5 hover:bg-gray-50 dark:hover:bg-neutral-800 transition-colors"
                  >
                    <div className="shrink-0 w-9 h-9 rounded-xl bg-[#970000]/10 dark:bg-red-500/15 flex items-center justify-center">
                      <Award className="h-4 w-4 text-[#970000] dark:text-red-400" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-semibold text-gray-900 dark:text-neutral-100 line-clamp-1">
                        {cert.title}
                      </p>
                      <p className="text-xs text-gray-400 dark:text-neutral-500 mt-0.5">
                        Ausgestellt am {new Date(cert.issued_at).toLocaleDateString('de-DE')}
                      </p>
                    </div>
                    <span className="inline-flex items-center gap-1 text-xs font-semibold text-[#970000] dark:text-red-400 shrink-0">
                      <Download className="h-3.5 w-3.5" />
                      Download
                    </span>
                  </button>
                </li>
              ))}
            </ul>
          </div>
        )}

        {/* Materials */}
        <div className="bg-white dark:bg-neutral-900 rounded-2xl border border-gray-200 dark:border-neutral-800 overflow-hidden">
          <div className="px-5 pt-5 pb-3 border-b border-gray-200 dark:border-neutral-800">
            <p className="text-[10px] font-bold tracking-[0.14em] uppercase text-gray-400 dark:text-neutral-500 mb-0.5">
              Bibliothek
            </p>
            <h2 className="text-lg font-bold text-gray-900 dark:text-neutral-100">Dateien &amp; Material</h2>
          </div>

          {materials.length === 0 ? (
            <div className="p-6 text-center">
              <p className="text-sm text-gray-400 dark:text-neutral-500">Noch keine Dateien hinterlegt.</p>
            </div>
          ) : (
            <ul className="divide-y divide-gray-200 dark:divide-neutral-800">
              {materials.map(mat => (
                <li key={mat.id}>
                  <button
                    type="button"
                    onClick={() =>
                      mat.material_type === 'pdf'
                        ? openFile('material-files', mat.storage_path, mat.url)
                        : window.open(mat.url, '_blank', 'noopener,noreferrer')
                    }
                    className="w-full text-left flex items-center gap-3 px-4 py-3.5 hover:bg-gray-50 dark:hover:bg-neutral-800 transition-colors"
                  >
                    <div className="shrink-0 w-9 h-9 rounded-xl bg-gray-50 dark:bg-neutral-800 flex items-center justify-center">
                      {mat.material_type === 'pdf' ? (
                        <FileText className="h-4 w-4 text-[#970000] dark:text-red-400" />
                      ) : (
                        <LinkIcon className="h-4 w-4 text-gray-500 dark:text-neutral-400" />
                      )}
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-semibold text-gray-900 dark:text-neutral-100 line-clamp-1">
                        {mat.title}
                      </p>
                      {mat.description && (
                        <p className="text-xs text-gray-400 dark:text-neutral-500 mt-0.5 line-clamp-1">
                          {mat.description}
                        </p>
                      )}
                    </div>
                    <div className="flex items-center gap-2 shrink-0">
                      {Date.now() - new Date(mat.created_at).getTime() < 14 * 24 * 60 * 60 * 1000 && (
                        <span className="text-[10px] font-bold tracking-wider uppercase bg-[#970000]/10 dark:bg-red-500/15 text-[#970000] dark:text-red-400 px-2 py-0.5 rounded-full">
                          Neu
                        </span>
                      )}
                      <span className="text-xs font-semibold text-[#970000] dark:text-red-400">
                        {mat.material_type === 'pdf' ? 'PDF' : 'Link'}
                      </span>
                    </div>
                  </button>
                </li>
              ))}
            </ul>
          )}
        </div>
      </div>
    </>
  );
};

export default StudentCoursePage;
